
import Vue from 'vue';
import { Component, Model, Prop, Watch } from 'vue-property-decorator';
import { AddressTypeEnum } from './Booking/models/IAddressViewModel';
import IGooglePlaceAddressComponents from './Booking/models/IGooglePlaceAddressComponents';

interface AddressItem {
  address: string;
  city?: string;
  description: string;
  postalcode?: string;
  placeId?: string;
  latitude?: number;
  longitude?: number;
  category?: string;
  type?: AddressTypeEnum;
}

@Component
export default class PassengerPlacePicker extends Vue {
  
  @Prop({ default: '' }) readonly placeholder!: string;
  @Prop({ default: false }) readonly disabled!: boolean;
  @Prop({ default: true }) readonly clearable!: boolean;
  @Prop({ default: '' }) readonly label!: string;

  searchInitialized: boolean = false;
  allowSearch: boolean = false;

  search: string = '';
  isLoading: boolean = false;
  items: AddressItem[] = [];

  googlePlacesAutocompleteService: any = null;
  googlePlacesService: any = null;
  autocompleteSessionToken: any = null;

  @Model('input', { type: Object }) readonly value!: AddressItem | null;
  internalValue: AddressItem | null = null;
    @Watch('value', { immediate: true })
    onValueChanged(val: AddressItem | null) {
      this.internalValue = val;
      if (val && val.address && !this.searchInitialized) {
        this.search = val.address;
        this.searchInitialized = true;
        this.allowSearch = false;
      }
    }




  get countryRestriction() {
    if (window.location.hostname.endsWith('co.uk')) return 'gb';
    if (window.location.hostname.endsWith('.us')) return 'us';
    return 'fi';
  }

  get placeholderText() {

    if(this.internalValue && this.internalValue.address) {
      return this.internalValue.address + ', '  + this.internalValue.city;
    }
    return this.$i18n.translate('generic.searchAddress');
  }

  mounted() {
    this.ensureGooglePlacesServicesCreated();
  }

  ensureGooglePlacesServicesCreated() {
    if (!this.autocompleteSessionToken) {
      this.autocompleteSessionToken = new (window as any).google.maps.places.AutocompleteSessionToken();
    }
    if (!this.googlePlacesAutocompleteService) {
      this.googlePlacesAutocompleteService = new (window as any).google.maps.places.AutocompleteService();
    }
    if (!this.googlePlacesService) {
      this.googlePlacesService = new (window as any).google.maps.places.PlacesService(
        this.$refs.googlePlacesContainer
      );
    }
  }

  getGooglePlacesPredictionsTimeout: number = 0;

  @Watch('search')
  onSearchChanged(val: string) {
    if (!this.allowSearch) {
      this.allowSearch = true;
      return;
    }
    if (!val) {
      this.items = [];
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    if (this.getGooglePlacesPredictionsTimeout) {
      window.clearTimeout(this.getGooglePlacesPredictionsTimeout);
    }
    this.getGooglePlacesPredictionsTimeout = window.setTimeout(() => {
      const request = {
        input: val,
        componentRestrictions: { country: this.countryRestriction },
        sessionToken: this.autocompleteSessionToken
      };
      this.googlePlacesAutocompleteService.getPlacePredictions(
        request,
        (predictions: any[], status: string) => {
          this.isLoading = false;
          if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && predictions) {
            this.items = predictions.map(p => ({
            
              address: p.description,
              description: p.description,
              placeId: p.place_id
            }));
          } else {
            this.items = [];
          }
        }
      );
    }, 400);
  }

  onSelect(val: AddressItem) {
    
    if (val && val.placeId) {
      this.isLoading = true;
      this.getGooglePlacesPlaceDetails(val.placeId, (result: any, status: google.maps.places.PlacesServiceStatus) => {
        this.isLoading = false;
        if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && result) {
          // Käytetään googlePlacesApiResultToAddressViewModel
          const address = this.googlePlacesApiResultToAddressViewModel(
            result,
            status,
            undefined
          );
          this.internalValue = address;
          this.$emit('input', address);
          this.$emit('address-selected', address);
        } else {
          this.$emit('input', val);
          this.$emit('address-selected', val);
        }
        this.autocompleteSessionToken = new (window as any).google.maps.places.AutocompleteSessionToken();
      });
    } else {
      this.$emit('input', val);
      this.$emit('address-selected', val);
    }
  }

  getGooglePlacesPlaceDetails(placeId: string, callback: (result: any, status: google.maps.places.PlacesServiceStatus) => void) {
    this.ensureGooglePlacesServicesCreated();
    const request = {
      placeId,
      sessionToken: this.autocompleteSessionToken,
      fields: [
        'address_components',
        'formatted_address',
        'geometry.location',
        'name',
        'place_id',
        'types',
        'vicinity'
      ]
    };
    this.googlePlacesService.getDetails(request, callback);
  }

  googlePlacesApiResultToAddressItem(result: any): AddressItem {
    return {
      address: result.formatted_address || result.name,
      description: result.name,
      placeId: result.place_id,
      latitude: result.geometry?.location?.lat(),
      longitude: result.geometry?.location?.lng(),
    };
  }

  googlePlacesApiResultToAddressViewModel(
    result: google.maps.places.PlaceResult,
    status: google.maps.places.PlacesServiceStatus,
    type: AddressTypeEnum,
  ): AddressItem {

    if (status !== google.maps.places.PlacesServiceStatus.OK) {
      alert(
        "Error: Google Places API: googlePlacesService.getDetails: " + status
      );
      return;
    }

    const addressComponents: IGooglePlaceAddressComponents = {};
    const addressComponentTypes: any = {
      street_number: "short_name",
      route: "long_name",
      locality: "long_name",
      administrative_area_level_1: "short_name",
      administrative_area_level_3: "short_name",
      country: "long_name",
      postal_code: "short_name"
    };

    result.address_components.forEach(ac => {
      const addressType = ac.types[0];  
      if (addressComponentTypes[addressType]) {
        const val = ac[addressComponentTypes[addressType]];
        addressComponents[addressType] = val;
      }
    });

    const placeType: string =
      result.types && result.types.length > 0 ? result.types[0] : "address";

    let streetAddress = result.formatted_address
      ? result.formatted_address.split(',')[0]
      : '';

    if (!streetAddress) {
      streetAddress = addressComponents.route;
    }

    const locality = addressComponents.locality ? addressComponents.locality : addressComponents.administrative_area_level_3;

    
    const description = result.name ?? result.formatted_address ?? '';


    const addressViewModel: AddressItem = {
      address: streetAddress,
      description: description,
      city: locality,
      postalcode: addressComponents.postal_code,
      latitude: typeof(result.geometry.location.lat) === 'number' 
          ? result.geometry.location.lat as unknown as number 
          : result.geometry.location.lat(),
      longitude: typeof(result.geometry.location.lng) === 'number' 
          ? result.geometry.location.lng as unknown as number 
          : result.geometry.location.lng(),
      type,
      category: placeType
    };
    return addressViewModel;
  }


  onClear() {
    this.internalValue = null;
    this.search = '';
    this.items = [];
    this.$emit('input', null);
    this.$emit('address-selected', null);
  }
}
